// What the signed-in person's home lists, as `GET /me/home` returns it:
// quotes to pick back up, policies, and arrangements. Read defensively: a
// row this version cannot read is dropped, never guessed at.

import { getStageDisplayDetails, hasReachedStage } from "helpers/at-need";

export type HomeQuoteKind = "pre_planning" | "cremation";
export type HomePolicyStatus = "finalizing" | "issued";
/** `minimal` is a case contact who is neither Informant nor Purchaser: they
 * are shown the loved one's name and the stage, and nothing else. */
export type HomeArrangementAccess = "full" | "minimal";

export interface HomeQuote {
  quoteId: string;
  kind: HomeQuoteKind;
  label: string;
  /** The case a cremation quote is for; null for any other quote. */
  caseId: string | null;
  /** The policy a pre-planning quote is for; null for any other quote. */
  policyId: string | null;
}

export interface HomePolicy {
  policyId: string;
  status: HomePolicyStatus;
  label: string;
}

export interface HomeArrangement {
  caseId: string;
  stage: string;
  access: HomeArrangementAccess;
  label: string;
  /** The active quote the person may open for the case; null when there is none. */
  quoteId: string | null;
}

export interface MeHome {
  quotes: HomeQuote[];
  policies: HomePolicy[];
  arrangements: HomeArrangement[];
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === "object" && raw !== null
    ? (raw as Record<string, unknown>)
    : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function labelOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function parseQuote(raw: unknown): HomeQuote | null {
  const row = asRecord(raw);
  const quoteId = nonEmptyString(row?.quote_id);
  if (!row || !quoteId) return null;
  if (row.kind !== "pre_planning" && row.kind !== "cremation") return null;
  return {
    quoteId,
    kind: row.kind,
    label: labelOf(row.label),
    caseId: nonEmptyString(row.case_id),
    policyId: nonEmptyString(row.policy_id),
  };
}

function parsePolicy(raw: unknown): HomePolicy | null {
  const row = asRecord(raw);
  const policyId = nonEmptyString(row?.policy_id);
  if (!row || !policyId) return null;
  if (row.status !== "finalizing" && row.status !== "issued") return null;
  return { policyId, status: row.status, label: labelOf(row.label) };
}

function parseArrangement(raw: unknown): HomeArrangement | null {
  const row = asRecord(raw);
  const caseId = nonEmptyString(row?.case_id);
  if (!row || !caseId) return null;
  return {
    caseId,
    stage: labelOf(row.stage),
    access: row.access === "full" ? "full" : "minimal",
    label: labelOf(row.label),
    quoteId: nonEmptyString(row.quote_id),
  };
}

function parseRows<Row>(
  raw: unknown,
  parseRow: (row: unknown) => Row | null,
): Row[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    const parsed = parseRow(row);
    return parsed ? [parsed] : [];
  });
}

/** An unknown access reads as `minimal`, the least the person may see. */
export function parseMeHome(raw: unknown): MeHome {
  const body = asRecord(raw);
  return {
    quotes: parseRows(body?.quotes, parseQuote),
    policies: parseRows(body?.policies, parsePolicy),
    arrangements: parseRows(body?.arrangements, parseArrangement),
  };
}

/** The id of the active quote the person may open for the case: the one the
 * case's arrangement row names, else the cremation quote that names the case.
 * A case's id and its quote's id are unrelated. */
export function quoteIdForCase(
  home: MeHome,
  caseId: string | null | undefined,
): string | null {
  if (!caseId) return null;
  const arranged = home.arrangements.find(
    (arrangement) => arrangement.caseId === caseId,
  );
  return arranged?.quoteId ?? quoteForCase(home, caseId)?.quoteId ?? null;
}

/**
 * The part of the home that is still under way: no case that has reached
 * Complete, no issued policy, and no quote for either. What a person can start
 * or resume, as opposed to everything they have.
 */
export function inProgressHome(home: MeHome): MeHome {
  const finishedCases = new Set(
    home.arrangements
      .filter((arrangement) => hasReachedStage(arrangement.stage, "Complete"))
      .map((arrangement) => arrangement.caseId),
  );
  const issuedPolicies = new Set(
    home.policies
      .filter((policy) => policy.status === "issued")
      .map((policy) => policy.policyId),
  );
  return {
    arrangements: home.arrangements.filter(
      (arrangement) => !finishedCases.has(arrangement.caseId),
    ),
    policies: home.policies.filter(
      (policy) => !issuedPolicies.has(policy.policyId),
    ),
    quotes: home.quotes.filter(
      (quote) =>
        !(quote.caseId !== null && finishedCases.has(quote.caseId)) &&
        !(quote.policyId !== null && issuedPolicies.has(quote.policyId)),
    ),
  };
}

export function homeIsEmpty(home: MeHome): boolean {
  return (
    home.quotes.length === 0 &&
    home.policies.length === 0 &&
    home.arrangements.length === 0
  );
}

/** What Home shows once loaded. A failed read keeps its error message and
 * is never mistaken for an empty account. */
export function homeBody(
  home: MeHome | null,
  failed: boolean,
): "failed" | "get_started" | "sections" {
  if (failed || home === null) return "failed";
  return homeIsEmpty(home) ? "get_started" : "sections";
}

/** What a surface that lists the home under a heading shows: a placeholder
 * while the read is out, the sections when anything is listed, nothing
 * otherwise (a failed read lists nothing). */
export function homeListingPhase(
  home: MeHome | null,
  isLoading: boolean,
): "loading" | "listed" | "empty" {
  if (isLoading) return "loading";
  return home !== null && !homeIsEmpty(home) ? "listed" : "empty";
}

export const QUOTE_PILL = "Quote";

export const POLICY_PILL: Record<HomePolicyStatus, string> = {
  finalizing: "Finalizing",
  issued: "Issued",
};

export function quotePathById(quoteId: string): string {
  return `/quotes/${encodeURIComponent(quoteId)}`;
}

export function quotePath(quote: HomeQuote): string {
  return quotePathById(quote.quoteId);
}

export function policyPathById(policyId: string): string {
  return `/policies/${encodeURIComponent(policyId)}`;
}

export function policyPath(policy: HomePolicy): string {
  return policyPathById(policy.policyId);
}

function casePath(caseId: string): string {
  return `/cases/${encodeURIComponent(caseId)}`;
}

/** The case page for a contact who can open the case or its quote (a Quote
 * Recipient sees the case's name, stage and quote there); null for one shown
 * without a way in. */
export function arrangementPath(arrangement: HomeArrangement): string | null {
  if (arrangement.access === "full" || arrangement.quoteId !== null) {
    return casePath(arrangement.caseId);
  }
  return null;
}

/** The cremation quote made for a case. A case's id and its quote's id are
 * unrelated, so the quote is found by the case it names. */
export function quoteForCase(
  home: MeHome,
  caseId: string | null | undefined,
): HomeQuote | null {
  if (!caseId) return null;
  return (
    home.quotes.find(
      (quote) => quote.kind === "cremation" && quote.caseId === caseId,
    ) ?? null
  );
}

/** One row of a Home section. `to` is null for a row shown without a way in. */
export interface HomeListing {
  key: string;
  to: string | null;
  name: string;
  pill: string;
}

/** Every case the person can see, each opening the case page: a case before
 * Dispatch that has a quote reads "Quote"; any other reads its stage, a
 * case before Dispatch with no quote included. A cremation quote whose case the person has no arrangement row for
 * still lists as that case. */
export function arrangementListings(home: MeHome): HomeListing[] {
  const { arrangements } = home;
  const listedCases = new Set(
    home.arrangements.map((arrangement) => arrangement.caseId),
  );
  const quoteOnly = home.quotes.filter(
    (quote) =>
      quote.kind === "cremation" &&
      !(quote.caseId !== null && listedCases.has(quote.caseId)),
  );
  return [
    ...quoteOnly.map((quote) => ({
      key: `quote:${quote.quoteId}`,
      to: quote.caseId === null ? quotePath(quote) : casePath(quote.caseId),
      name: quote.label || "Your quote",
      pill: QUOTE_PILL,
    })),
    ...arrangements.map((arrangement) => ({
      key: `case:${arrangement.caseId}`,
      to: arrangementPath(arrangement),
      name: arrangement.label || "Loved One Name N/A",
      pill:
        !hasReachedStage(arrangement.stage, "Dispatch") &&
        quoteIdForCase(home, arrangement.caseId) !== null
          ? QUOTE_PILL
          : getStageDisplayDetails(arrangement.stage).name,
    })),
  ];
}

/** Where a pre-planning quote's row opens: its policy's page, which leads
 * on to the quote; the quote itself when no policy is named. */
function preplanningQuotePath(quote: HomeQuote): string {
  return quote.policyId === null
    ? quotePath(quote)
    : policyPathById(quote.policyId);
}

/** Pre-planning quotes and completed policies. A quote whose policy is
 * listed gives way to that policy. */
export function planningListings(home: MeHome): HomeListing[] {
  const listedPolicies = new Set(
    home.policies.map((policy) => policy.policyId),
  );
  return [
    ...home.quotes
      .filter(
        (quote) =>
          quote.kind === "pre_planning" &&
          !(quote.policyId !== null && listedPolicies.has(quote.policyId)),
      )
      .map((quote) => ({
        key: `quote:${quote.quoteId}`,
        to: preplanningQuotePath(quote),
        name: quote.label || "Your quote",
        pill: QUOTE_PILL,
      })),
    ...home.policies.map((policy) => ({
      key: `policy:${policy.policyId}`,
      to: policyPath(policy),
      name: policy.label || "Your policy",
      pill: POLICY_PILL[policy.status],
    })),
  ];
}

export type SidebarEntryKind = "at_need" | "pre_need";

export interface SidebarEntry {
  to: string;
  label: string;
  kind: SidebarEntryKind;
}

function entriesOf(
  listings: HomeListing[],
  kind: SidebarEntryKind,
): SidebarEntry[] {
  return listings.flatMap((listing) =>
    listing.to === null ? [] : [{ to: listing.to, label: listing.name, kind }],
  );
}

/** The sidebar's entries from the home: Home's rows that can be opened,
 * arrangements (heart) before pre-planning (briefcase), each linking where
 * its Home row does. */
export function sidebarEntries(home: MeHome): SidebarEntry[] {
  return [
    ...entriesOf(arrangementListings(home), "at_need"),
    ...entriesOf(planningListings(home), "pre_need"),
  ];
}

/** The record kinds Home can send a person straight into. Each maps a home to
 * the detail path of every record of that kind (null for one shown without a
 * way in). A new kind of record is one more entry. */
const RECORD_DETAIL_PATHS: Array<(home: MeHome) => Array<string | null>> = [
  (home) => home.arrangements.map(arrangementPath),
  (home) => home.policies.map(policyPath),
];

/**
 * Where Home sends the person once their home is read: the detail page of
 * their only record, or null to stay on Home. Cases and policies count
 * together, so one case and one policy stay on Home. Anything else Home lists
 * (a quote with no record, a record shown without a way in) keeps them on Home.
 * Never a target while the read is out or after it failed.
 */
export function singleRecordTarget(
  home: MeHome | null,
  isLoading: boolean,
  failed: boolean,
): string | null {
  if (isLoading || failed || home === null) return null;
  const paths = RECORD_DETAIL_PATHS.flatMap((pathsOf) => pathsOf(home));
  const listed =
    arrangementListings(home).length + planningListings(home).length;
  if (paths.length !== 1 || listed !== 1) return null;
  return paths[0];
}
